import { useEffect } from "react";

/**
 * SiteClosed — full-screen holding page shown in place of the entire app.
 *
 * While this is rendered (see src/index.js), every route resolves to this page
 * and no part of the builder/dashboard/auth is reachable and no API calls are made.
 *
 * TO BRING THE SITE BACK: in src/index.js, render <App /> again instead of
 * <SiteClosed /> (one line). This file can then be left in place or deleted.
 *
 * Styles are scoped under `.ghc-*` to avoid clashing with index.css.
 */
export default function SiteClosed() {
  useEffect(() => {
    document.title = "GoldenHour";
  }, []);

  return (
    <div className="ghc-root">
      <style>{`
        .ghc-root{
          --bg:#0C0B09; --fg:#F0EDE5; --gold:#C9A84C; --gold-light:#E8D5A3;
          --muted:rgba(240,237,229,0.55); --stone:#8c8782;
          --serif:'Instrument Serif',Georgia,serif; --sans:'Outfit',system-ui,sans-serif; --mono:'Space Mono',monospace;
          position:fixed; inset:0; overflow:hidden; background:var(--bg); color:var(--fg);
          font-family:var(--sans); font-weight:300; -webkit-font-smoothing:antialiased;
        }

        /* warm light-leak glows (radial "exposure" leaks, no bg gradients) */
        .ghc-glow{position:absolute;inset:0;pointer-events:none;z-index:0;overflow:hidden}
        .ghc-glow::before{
          content:'';position:absolute;width:900px;height:900px;left:50%;top:58%;transform:translate(-50%,-50%);
          background:radial-gradient(circle,rgba(201,168,76,0.11) 0%,rgba(191,91,59,0.05) 38%,transparent 66%);
          animation:ghc-breathe 14s ease-in-out infinite;
        }
        .ghc-glow::after{
          content:'';position:absolute;width:600px;height:600px;right:-8%;top:-12%;
          background:radial-gradient(circle,rgba(191,91,59,0.07) 0%,transparent 62%);
          animation:ghc-drift 26s ease-in-out infinite alternate;
        }
        @keyframes ghc-breathe{0%,100%{opacity:.75;transform:translate(-50%,-50%) scale(1)}50%{opacity:1;transform:translate(-50%,-50%) scale(1.06)}}
        @keyframes ghc-drift{0%{transform:translate(0,0)}100%{transform:translate(-6vw,4vh)}}

        /* film grain */
        .ghc-grain{
          position:absolute;inset:0;pointer-events:none;z-index:1;opacity:.055;mix-blend-mode:overlay;
          background-image:url("data:image/svg+xml,%3Csvg viewBox='0 0 512 512' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='5' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E");
          background-size:200px;animation:ghc-grain .3s steps(6) infinite;
        }
        @keyframes ghc-grain{0%{transform:translate(0,0)}16%{transform:translate(-3px,1px)}33%{transform:translate(2px,-2px)}50%{transform:translate(-1px,3px)}66%{transform:translate(3px,-1px)}83%{transform:translate(-2px,-2px)}}

        .ghc-stage{position:relative;z-index:2;height:100%;display:flex;flex-direction:column;align-items:center;justify-content:center;text-align:center;padding:40px 24px}
        .ghc-inner{width:min(680px,100%);display:flex;flex-direction:column;align-items:center}

        .ghc-rise{opacity:0;transform:translateY(22px);filter:blur(6px);animation:ghc-rise 1.5s cubic-bezier(.25,.46,.45,.94) forwards}
        .ghc-d1{animation-delay:.15s}.ghc-d2{animation-delay:.4s}.ghc-d3{animation-delay:.7s}.ghc-d4{animation-delay:1s}.ghc-d5{animation-delay:1.3s}
        @keyframes ghc-rise{to{opacity:1;transform:none;filter:blur(0)}}

        .ghc-sun-wrap{margin-bottom:38px}
        .ghc-sun{display:block}
        .ghc-sun .ghc-disc{transform-origin:center;animation:ghc-sink 9s ease-in-out infinite}
        @keyframes ghc-sink{0%,100%{transform:translateY(0)}50%{transform:translateY(4px)}}

        .ghc-wordmark{font-family:var(--serif);font-size:22px;letter-spacing:.01em;color:var(--fg);margin-bottom:34px}
        .ghc-wordmark em{font-style:italic;color:var(--gold)}

        .ghc-head{
          font-family:var(--serif);font-weight:400;font-style:italic;
          font-size:clamp(40px,8.5vw,92px);line-height:.98;letter-spacing:-.02em;
          color:var(--fg);max-width:min(14ch,100%);
        }
        .ghc-head .ghc-soft{color:var(--gold-light)}

        .ghc-body{
          font-family:var(--sans);font-weight:200;font-size:clamp(15px,1.6vw,18px);line-height:1.7;
          color:var(--muted);max-width:min(46ch,100%);margin:28px auto 0;
        }

        .ghc-divider{width:56px;height:1px;background:linear-gradient(90deg,transparent,var(--gold),transparent);margin:44px auto 0;opacity:.7}

        .ghc-foot{margin-top:26px;font-family:var(--mono);font-size:10px;letter-spacing:.22em;text-transform:uppercase;color:var(--stone)}
        .ghc-foot span{color:var(--muted)}

        @media (max-width:600px){
          .ghc-stage{padding:32px 22px}
          .ghc-wordmark{margin-bottom:26px}
          .ghc-divider{margin-top:34px}
        }
        @media (prefers-reduced-motion:reduce){
          .ghc-glow::before,.ghc-glow::after,.ghc-grain,.ghc-sun .ghc-disc{animation:none}
          .ghc-rise{animation:none;opacity:1;transform:none;filter:none}
        }
      `}</style>

      <div className="ghc-glow" />
      <div className="ghc-grain" />

      <main className="ghc-stage">
        <div className="ghc-inner">
          <div className="ghc-sun-wrap ghc-rise ghc-d1">
            <svg className="ghc-sun" width="92" height="60" viewBox="0 0 92 60" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
              <g stroke="#C9A84C" strokeWidth="1.4" strokeLinecap="round" opacity="0.75">
                <path d="M46 4 V13" />
                <path d="M22 12 L27 19" />
                <path d="M70 12 L65 19" />
                <path d="M6 30 H15" />
                <path d="M86 30 H77" />
              </g>
              <circle className="ghc-disc" cx="46" cy="34" r="15" stroke="#E8D5A3" strokeWidth="1.6" />
              <line x1="4" y1="48" x2="88" y2="48" stroke="#C9A84C" strokeWidth="1.4" strokeLinecap="round" opacity="0.55" />
            </svg>
          </div>

          <div className="ghc-wordmark ghc-rise ghc-d2">golden<em>hour</em></div>

          <h1 className="ghc-head ghc-rise ghc-d3">The light has <span className="ghc-soft">faded.</span></h1>

          <p className="ghc-body ghc-rise ghc-d4">
            GoldenHour has closed. Thank you to everyone who chased the light with us —
            the photographers, the videographers, the storytellers.
          </p>

          <div className="ghc-divider ghc-rise ghc-d5" />
          <div className="ghc-foot ghc-rise ghc-d5">© 2026 GoldenHour — <span>by Uncut Gems</span></div>
        </div>
      </main>
    </div>
  );
}
